import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Activity, Database, Building2, Receipt, Key, AlertTriangle, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { api } from '../../api/client';
import { loadSession } from '../../auth/auth';
import { OwnCompanyEfakturaPanel } from './OwnCompanyEfakturaPanel';

type Metrics = {
  tenants: { total: number; suspended: number };
  locations: { total: number };
  users: { total: number; active: number };
  operators: { total: number; active: number };
  sales: { total: number; last24h: number };
  sysadmins: { total: number; active: number };
};

type Health = {
  status: 'ok' | 'degraded';
  database: { status: 'ok' | 'error'; latencyMs: number };
  uptimeSec: number;
};

type LicenseStatus = {
  expired: Array<{ id: string; tenantName: string | null; moduleCode: string }>;
  expiringSoon: Array<{ id: string; tenantName: string | null; moduleCode: string }>;
};

type AuditSummary = {
  total: number;
  windowDays: number;
};

export function DashboardPage() {
  // Prodavac nema pristup platformskim metrikama (ADR-026) - ranije je video
  // same crtice, „Ucitavanje..." zauvek i linkove na strane koje mu server odbija.
  if (loadSession()?.admin.role === 'reseller') return <ResellerDashboard />;
  return <PlatformDashboard />;
}

function PlatformDashboard() {
  const [loaded, setLoaded] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null);
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Metrics>('/admin/system/metrics').then(setMetrics).catch(() => null),
      api.get<Health>('/admin/system/health').then(setHealth).catch(() => null),
      api.get<LicenseStatus>('/admin/system/licenses-status').then(setLicenseStatus).catch(() => null),
      api.get<AuditSummary>('/admin/system/audit-summary').then(setAuditSummary).catch(() => null),
    ]).catch((e) => setError(e?.message || 'Greška')).finally(() => setLoaded(true));
  }, []);

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Pregled</h1>
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>Status sistema i ključne metrike</div>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Top KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14, marginBottom: 24 }}>
        <KpiCard
          icon={Building2}
          label="Klijenti"
          value={metrics?.tenants.total ?? '-'}
          sub={metrics && metrics.tenants.suspended > 0 ? `${metrics.tenants.suspended} suspendovano` : 'Svi aktivni'}
          color="#3b82f6"
          to="/tenants"
        />
        <KpiCard
          icon={MapPin}
          label="Lokacije"
          value={metrics?.locations.total ?? '-'}
          color="#a78bfa"
          to="/locations"
        />
        <KpiCard
          icon={Users}
          label="Operateri"
          value={metrics?.operators.active ?? '-'}
          sub={metrics ? `${metrics.operators.total} ukupno` : undefined}
          color="#10b981"
          to="/operators"
        />
        <KpiCard
          icon={Receipt}
          label="Računi (24h)"
          value={metrics?.sales.last24h ?? '-'}
          sub={metrics ? `${metrics.sales.total} ukupno` : undefined}
          color="#f59e0b"
        />
        <KpiCard
          icon={Activity}
          label={`Audit eventi (${auditSummary?.windowDays ?? 7}d)`}
          value={auditSummary?.total ?? '-'}
          color="#ec4899"
          to="/audit"
        />
        <KpiCard
          icon={Key}
          label="Sysadmin nalozi"
          value={metrics?.sysadmins.active ?? '-'}
          sub={metrics ? `${metrics.sysadmins.total} ukupno` : undefined}
          color="#8b5cf6"
          to="/users"
        />
      </div>

      {/* System health row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
        <div style={panel}>
          <div style={panelHeader}>
            <Database size={16} style={{ color: '#60a5fa' }} />
            <span>Sistem health</span>
            <Link to="/system" style={panelLink}>Detalji <ExternalLink size={11} /></Link>
          </div>
          {health ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16 }}>
              <Row label="API status">
                {health.status === 'ok'
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', fontWeight: 600 }}><CheckCircle2 size={14} /> OK</span>
                  : <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#f59e0b', fontWeight: 600 }}><AlertTriangle size={14} /> Degraded</span>}
              </Row>
              <Row label="Baza">
                {health.database.status === 'ok'
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', fontWeight: 600 }}><CheckCircle2 size={14} /> {health.database.latencyMs} ms</span>
                  : <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontWeight: 600 }}><XCircle size={14} /> Error</span>}
              </Row>
              <Row label="Uptime">
                <span style={{ fontFamily: 'monospace', color: 'var(--color-text)' }}>{formatUptime(health.uptimeSec)}</span>
              </Row>
            </div>
          ) : (
            <div style={{ padding: 16, color: 'var(--color-text-muted)', fontSize: 13 }}>{loaded ? 'Nije dostupno' : 'Učitavanje...'}</div>
          )}
        </div>

        <div style={panel}>
          <div style={panelHeader}>
            <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
            <span>Licence upozorenja</span>
            <Link to="/system" style={panelLink}>Detalji <ExternalLink size={11} /></Link>
          </div>
          {licenseStatus ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16 }}>
              <Row label="Istekle">
                <span style={{ color: licenseStatus.expired.length > 0 ? '#fca5a5' : 'var(--color-text-muted)', fontWeight: 600 }}>
                  {licenseStatus.expired.length}
                </span>
              </Row>
              <Row label="Ističu (30 dana)">
                <span style={{ color: licenseStatus.expiringSoon.length > 0 ? '#fcd34d' : 'var(--color-text-muted)', fontWeight: 600 }}>
                  {licenseStatus.expiringSoon.length}
                </span>
              </Row>
              {licenseStatus.expired.length === 0 && licenseStatus.expiringSoon.length === 0 && (
                <div style={{ fontSize: 12, color: '#10b981', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle2 size={13} /> Sve licence su validne
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 16, color: 'var(--color-text-muted)', fontSize: 13 }}>{loaded ? 'Nije dostupno' : 'Učitavanje...'}</div>
          )}
        </div>
      </div>

      {/* E-fakture naše firme - poslovne knjige pružaoca, samo za ulogu admin (server isto proverava). */}
      {loadSession()?.admin.role === 'admin' && <OwnCompanyEfakturaPanel />}

      {/* Quick links */}
      <div style={panel}>
        <div style={panelHeader}>
          <span>Brzi linkovi</span>
        </div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: 10 }}>
          <QuickLink to="/tenants" label="Upravljanje klijentima" desc="Kreiraj nove tenant-e i upravljaj postojećim" />
          <QuickLink to="/locations" label="Lokacije i POS režimi" desc="Promeni stock mod ili POS layout lokacije" />
          <QuickLink to="/operators" label="POS operateri" desc="Dodaj/izmeni operatere koji rade za kasom" />
          <QuickLink to="/audit" label="Audit log" desc="Pregled svih sistemskih događaja" />
          <QuickLink to="/license-packages" label="License paketi" desc="Predefinisani paketi sa kombinacijama modula" />
          <QuickLink to="/system" label="Sistem dijagnostika" desc="Health, metrike i monitoring licenci" />
        </div>
      </div>
    </div>
  );
}

/** Pregled za prodavca: brojke njegovih klijenata, bez platformskih podataka. */
function ResellerDashboard() {
  const [stats, setStats] = useState<{ tenants: number; suspended: number; locations: number; operators: number; openTickets: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Array<{ suspended?: boolean }>>('/admin/tenants'),
      api.get<unknown[]>('/admin/locations'),
      api.get<Array<{ isActive?: boolean }>>('/admin/operators'),
      api.get<{ open: number; inProgress: number }>('/admin/support/stats'),
    ]).then(([t, l, o, s]) => setStats({
      tenants: t.length,
      suspended: t.filter((x) => x.suspended).length,
      locations: l.length,
      operators: o.filter((x) => x.isActive !== false).length,
      openTickets: s.open + s.inProgress,
    })).catch((e) => setError(e?.message || 'Greška pri učitavanju'));
  }, []);

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Pregled</h1>
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>Vaši klijenti</div>
      </header>
      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14, marginBottom: 24 }}>
        <KpiCard icon={Building2} label="Klijenti" value={stats?.tenants ?? '-'}
          sub={stats && stats.suspended > 0 ? `${stats.suspended} suspendovano` : 'Svi aktivni'} color="#3b82f6" to="/tenants" />
        <KpiCard icon={MapPin} label="Lokacije" value={stats?.locations ?? '-'} color="#a78bfa" to="/locations" />
        <KpiCard icon={Users} label="Operateri" value={stats?.operators ?? '-'} color="#10b981" to="/operators" />
        <KpiCard icon={AlertTriangle} label="Otvorene prijave" value={stats?.openTickets ?? '-'} color="#f59e0b" to="/support" />
      </div>
      <div style={panel}>
        <div style={panelHeader}><span>Brzi linkovi</span></div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: 10 }}>
          <QuickLink to="/tenants" label="Klijenti" desc="Novi klijent, licence, korisnici i sertifikati" />
          <QuickLink to="/locations" label="Lokacije" desc="Objekti klijenata i POS režimi" />
          <QuickLink to="/operators" label="Operateri" desc="Operateri koji rade za kasom" />
          <QuickLink to="/support" label="Prijave problema" desc="Prijave vaših klijenata" />
        </div>
      </div>
    </div>
  );
}

function formatUptime(sec: number): string {
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function KpiCard({ icon: Icon, label, value, sub, color, to }: { icon: any; label: string; value: number | string; sub?: string; color: string; to?: string }) {
  const content = (
    <div style={{
      padding: 16,
      background: 'var(--color-bg-surface)',
      borderRadius: 10,
      border: '1px solid var(--color-border)',
      display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
      textDecoration: 'none', color: 'inherit',
      cursor: to ? 'pointer' : 'default',
      transition: 'border-color 0.15s',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: `${color}22`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-strong)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
          {typeof value === 'number' ? value.toLocaleString('sr-RS') : value}
        </div>
        {sub && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none', display: 'flex' }}>{content}</Link> : content;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      {children}
    </div>
  );
}

function QuickLink({ to, label, desc }: { to: string; label: string; desc: string }) {
  return (
    <Link to={to} style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: 12, borderRadius: 8,
      background: 'var(--color-bg)',
      border: '1px solid var(--color-border)',
      textDecoration: 'none',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)' }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{desc}</div>
    </Link>
  );
}

const panel: React.CSSProperties = {
  background: 'var(--color-bg-surface)',
  borderRadius: 10,
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
};

const panelHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)',
};

const panelLink: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: 500,
  color: 'var(--color-text-muted)',
  textDecoration: 'none',
};

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 14px', borderRadius: 6,
  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
  fontSize: 13, marginBottom: 16,
};
